import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { UserPlus, Search, X, Users, SlidersHorizontal, LayoutGrid, CheckCircle2, Clock3, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { PageState } from '@/components/layout/PageState';
import { HeroQuickMenuCard } from '@/components/shared/HeroQuickMenuCard';
import { AddCelebrationGuestsPanel } from '@/components/guests/AddCelebrationGuestsPanel';
import { GuestList } from '@/components/guests/GuestList';
import { GuestDetailsModal } from '@/components/guests/GuestDetailsModal';
import { GroupManager } from '@/components/guests/GroupManager';
import { GroupBadge } from '@/components/guests/GroupBadge';
import { guestService } from '@/lib/api/guest.service';
import { groupService } from '@/lib/api/group.service';
import { seatingApi } from '@/lib/api/seating.api';
import { SeatingBoard } from '@/components/seating/SeatingBoard';
import { useTeamMemberRole } from '@/hooks/useTeamMemberRole';
import { canAddRemoveGuests, canManageGuests } from '@/utils/permissions';
import type { Guest, CreateGuestDto } from '@/types/guest.types';
import { RSVPStatus } from '@/types/guest.types';
import type { SeatingVisualizationPayload } from '@/types/seating-v2.types';

export const GuestManagement: React.FC = () => {
  const { t } = useTranslation(['guests', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = parseInt(id || '0');

  // Hash-based tab routing: #seating → seating tab, default → guests
  const activeTab = location.hash === '#seating' ? 'seating' : 'guests';

  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<RSVPStatus | 'all'>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingGuest, setEditingGuest] = useState<Guest | null>(null);
  const [celebrationId, setCelebrationId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CreateGuestDto>({
    celebration_id: 0,
    event_id: eventId,
    name: '',
    is_additional_guest_allowed: false,
    additional_guest_type: 'count',
    additional_guest_count: 0,
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showAddGuestsPanel, setShowAddGuestsPanel] = useState(false);
  const [guestDetailsOpen, setGuestDetailsOpen] = useState(false);
  const [guestDetails, setGuestDetails] = useState<Guest | null>(null);
  const [guestDetailsLoading, setGuestDetailsLoading] = useState(false);
  const [guestDetailsError, setGuestDetailsError] = useState<string | null>(null);
  const [showGroupPanel, setShowGroupPanel] = useState(false);
  const [groupFilterGuestIds, setGroupFilterGuestIds] = useState<number[] | null>(null);
  const [activeGroupQuery, setActiveGroupQuery] = useState('');
  const [managingGroupId, setManagingGroupId] = useState<number | null>(null);
  const [managingGroupName, setManagingGroupName] = useState('');
  const [groupMemberIds, setGroupMemberIds] = useState<number[]>([]);
  const [savingMembers, setSavingMembers] = useState(false);
  const [groupRefreshKey, setGroupRefreshKey] = useState(0);
  const [eventTitle, setEventTitle] = useState('');

  // ---- Seating visualization state ----
  const [vizData, setVizData] = useState<SeatingVisualizationPayload | null>(null);
  const { role } = useTeamMemberRole(celebrationId);
  const canAddGuests = canAddRemoveGuests(role);
  const canEditGuests = canManageGuests(role);

  // Derived: guestId → seat label map
  const seatAssignments = useMemo(() => {
    const map = new Map<number, string>();
    if (vizData?.hasGeneratedLayout) {
      for (const unit of vizData.units) {
        for (const seat of unit.seats) {
          if (seat.assignedGuestId) {
            map.set(seat.assignedGuestId, `${unit.label} · ${seat.label}`);
          }
        }
      }
    }
    return map;
  }, [vizData]);

  const loadGuests = useCallback(async (cacheBust = false, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [data, eventDetails] = await Promise.all([
        guestService.getEventGuests(eventId, cacheBust),
        guestService.getEventDetails(eventId).catch(() => null),
      ]);
      setGuests(data);
      if (eventDetails) {
        setCelebrationId(eventDetails.celebration_id);
        setEventTitle(eventDetails.title);
        setFormData(prev => ({ ...prev, celebration_id: eventDetails.celebration_id }));
      }
      // Get celebration_id from first guest
      if (data.length > 0) {
        setCelebrationId(data[0].celebration_id);
        setFormData(prev => ({ ...prev, celebration_id: data[0].celebration_id }));
      }
      setError(null);
    } catch {
      setError('Failed to load guests');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Load visualization to populate seat assignments
  const loadVisualization = useCallback(async () => {
    try {
      const response = await seatingApi.getVisualizationV2(eventId);
      if (response.success) setVizData(response.data);
    } catch {
      // No layout yet — that's ok
    }
  }, [eventId]);

  const handleAssignmentChange = useCallback(async () => {
    await loadVisualization();
  }, [loadVisualization]);

  useEffect(() => {
    loadGuests();
  }, [loadGuests]);

  useEffect(() => {
    loadVisualization();
  }, [loadVisualization]);

  const filteredGuests = guests.filter(g => {
    const matchesSearch = searchTerm.startsWith('#')
      ? true // Group search is handled by groupFilterGuestIds
      : (g.name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesStatus = statusFilter === 'all' || g.rsvp_status === statusFilter;
    const matchesGroup = groupFilterGuestIds === null || groupFilterGuestIds.includes(g.id);
    return matchesSearch && matchesStatus && matchesGroup;
  });

  // Handle # search in the search bar
  const handleSearchChange = async (value: string) => {
    setSearchTerm(value);
    if (value.startsWith('#') && value.length > 1 && celebrationId) {
      try {
        const result = await groupService.searchByGroups(celebrationId, value);
        setGroupFilterGuestIds(result.guest_ids);
        setActiveGroupQuery(value);
      } catch {
        // If search fails, don't filter
        setGroupFilterGuestIds(null);
        setActiveGroupQuery('');
      }
    } else if (!value.startsWith('#')) {
      // Clear group filter when not using # prefix
      if (groupFilterGuestIds !== null && !activeGroupQuery) {
        setGroupFilterGuestIds(null);
      }
      if (!activeGroupQuery) {
        setGroupFilterGuestIds(null);
      }
    }
  };

  const handleGroupFilter = (guestIds: number[] | null, query: string) => {
    setGroupFilterGuestIds(guestIds);
    setActiveGroupQuery(query);
    if (query) {
      setSearchTerm(query);
    } else {
      setSearchTerm('');
    }
  };

  const clearGroupFilter = () => {
    setGroupFilterGuestIds(null);
    setActiveGroupQuery('');
    setSearchTerm('');
  };

  // Group member management using the existing guest list checkboxes
  const handleManageMembers = async (groupId: number, groupName: string) => {
    if (managingGroupId === groupId) {
      // Toggle off
      setManagingGroupId(null);
      setManagingGroupName('');
      setGroupMemberIds([]);
      return;
    }
    setManagingGroupId(groupId);
    setManagingGroupName(groupName);
    try {
      const members = await groupService.getMembers(celebrationId!, groupId);
      setGroupMemberIds(members.map(m => m.guest_id));
    } catch {
      setGroupMemberIds([]);
    }
  };

  const handleGroupMemberToggle = (guestId: number, checked: boolean) => {
    setGroupMemberIds(prev =>
      checked ? [...prev, guestId] : prev.filter(id => id !== guestId)
    );
  };

  const handleSaveGroupMembers = async () => {
    if (!managingGroupId || !celebrationId) return;
    setSavingMembers(true);
    try {
      await groupService.setMembers(celebrationId, managingGroupId, groupMemberIds);
      setManagingGroupId(null);
      setManagingGroupName('');
      setGroupMemberIds([]);
      setGroupRefreshKey(k => k + 1);
    } catch {
      setError('Failed to save group members');
    } finally {
      setSavingMembers(false);
    }
  };

  const handleCancelGroupMembers = () => {
    setManagingGroupId(null);
    setManagingGroupName('');
    setGroupMemberIds([]);
  };

  const stats = {
    total: guests.length,
    accepted: guests.filter(g => g.rsvp_status === RSVPStatus.ACCEPTED).length,
    declined: guests.filter(g => g.rsvp_status === RSVPStatus.DECLINED).length,
    invited: guests.filter(g => g.rsvp_status === RSVPStatus.INVITED).length,
    new: guests.filter(g => g.rsvp_status === RSVPStatus.NEW).length,
  };


  const resetForm = () => {
    setEditingGuest(null);
    setFormData({
      celebration_id: celebrationId || 0,
      event_id: eventId,
      name: '',
      is_additional_guest_allowed: false,
      additional_guest_type: 'count',
      additional_guest_count: 0,
      notes: ''
    });
    setShowAddForm(false);
    setFormErrors({});
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (formData.name && !formData.name.trim()) errors.name = 'Name cannot be empty if provided';
    if ((formData.additional_guest_count ?? 0) < 0) errors.additional_guests = 'Cannot be negative';
    if (formData.is_additional_guest_allowed && (formData.additional_guest_count ?? 0) < 1) {
      errors.additional_guests = 'Additional guest count must be at least 1';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      if (editingGuest) {
        await guestService.updateGuest(eventId, editingGuest.id, formData);
      } else {
        await guestService.createGuest(eventId, formData);
      }
      await loadGuests(true);
      resetForm();
    } catch {
      alert('Failed to save guest.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkGuestsSaved = async () => {
    await loadGuests(true, true);
  };

  const showGuestDetails = (guestId: number) => {
    setGuestDetailsLoading(true);
    guestService.getGuestDetails(guestId)
      .then((guest) => {
        setGuestDetails(guest);
        setGuestDetailsOpen(true);
        setGuestDetailsError(null);
      })
      .catch(() => {
        setGuestDetailsError('Failed to load guest details');
      })
      .finally(() => setGuestDetailsLoading(false));
  };

  const closeGuestDetailsModal = () => {
    setGuestDetailsOpen(false);
    setGuestDetails(null);
    setGuestDetailsError(null);
  };

  const handleEditGuest = (guest: Guest) => {
    if (!canEditGuests) return;
    setEditingGuest(guest);
    setFormData({
      celebration_id: guest.celebration_id,
      event_id: guest.event_id,
      name: guest.name || '',
      is_additional_guest_allowed: guest.is_additional_guest_allowed ?? false,
      additional_guest_type: guest.additional_guest_type || 'count',
      additional_guest_count: guest.additional_guest_count || 0,
      notes: guest.notes || ''
    });
    setShowAddForm(true);
  };

  const handleDeleteGuest = async (guest: Guest) => {
    if (!canAddGuests) return;
    if (!window.confirm(`Delete "${guest.name}"?`)) return;
    try {
      await guestService.deleteGuest(eventId, guest.id);
      await loadGuests(true);
    } catch {
      alert('Failed to delete guest.');
    }
  };

  if (loading) {
    return (
      <PageState
        kind="loading"
        width={activeTab === 'seating' ? 'wide' : 'default'}
        title="Loading guests"
        description="Please wait while we load guest details."
      />
    );
  }

  return (
    <PageContainer width={activeTab === 'seating' ? 'wide' : 'default'} className="space-y-4">
      <AppBreadcrumbs
        items={[
          { label: t('common:nav.home'), to: '/home' },
          { label: t('common:nav.events'), to: '/events' },
          { label: 'Event', to: `/events/${eventId}` },
          { label: t('guests:title') },
        ]}
      />

      <HeroQuickMenuCard
        borderClassName="border-primary/20"
        heroClassName="bg-gradient-to-br from-primary/10 via-primary/[0.04] to-white"
        heroContent={
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center flex-shrink-0">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-foreground">{t('guests:title')}</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Manage guest list, RSVPs, and seating assignments for this event.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate(`/events/${eventId}`)}>
                Back to Event
              </Button>
              {canAddGuests && (
                <Button size="sm" onClick={() => setShowAddGuestsPanel(true)} disabled={!celebrationId}>
                  <UserPlus className="h-3 w-3 mr-1" />
                  Add guests
                </Button>
              )}
            </div>
          </div>
        }
        columns={6}
        items={[
          {
            key: 'total',
            icon: Users,
            iconClassName: 'text-blue-600',
            hoverClassName: 'hover:bg-blue-50',
            label: 'Total',
            value: stats.total,
          },
          {
            key: 'accepted',
            icon: CheckCircle2,
            iconClassName: 'text-emerald-600',
            hoverClassName: 'hover:bg-emerald-50',
            label: 'Accepted',
            value: stats.accepted,
          },
          {
            key: 'invited',
            icon: Clock3,
            iconClassName: 'text-amber-600',
            hoverClassName: 'hover:bg-amber-50',
            label: 'Invited',
            value: stats.invited,
          },
          {
            key: 'declined',
            icon: XCircle,
            iconClassName: 'text-rose-600',
            hoverClassName: 'hover:bg-rose-50',
            label: 'Declined',
            value: stats.declined,
          },
          {
            key: 'new',
            icon: UserPlus,
            iconClassName: 'text-cyan-600',
            hoverClassName: 'hover:bg-cyan-50',
            label: 'New',
            value: stats.new,
          },
          {
            key: 'seating',
            icon: LayoutGrid,
            iconClassName: 'text-violet-600',
            hoverClassName: 'hover:bg-violet-50',
            label: 'Seating',
            value: vizData?.hasGeneratedLayout
              ? `${vizData.assignedCount}/${vizData.totalSeats}`
              : 'Not Set',
            onClick: () => navigate(`/events/${eventId}/guests#seating`),
          },
        ]}
      />

      {/* Tab Bar */}
      <div className="border-b flex gap-0">
        <Link
          to="#guests"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
            activeTab === 'guests'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => navigate(`/events/${eventId}/guests`)}
        >
          <Users className="h-4 w-4 inline mr-1.5 -mt-0.5" />
          {t('guests:title')}
        </Link>
        <Link
          to="#seating"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
            activeTab === 'seating'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => navigate(`/events/${eventId}/guests#seating`)}
        >
          <LayoutGrid className="h-4 w-4 inline mr-1.5 -mt-0.5" />
          Seating Layout
        </Link>
      </div>

      {/* ===================== GUESTS TAB ===================== */}
      {activeTab === 'guests' && <>

      {/* Seating assignment quick-link (shown when a layout exists) */}
      {vizData?.hasGeneratedLayout && (
        <div className="flex items-center gap-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <LayoutGrid className="h-4 w-4 text-blue-500 flex-shrink-0" />
          <span className="text-blue-700">
            Seating layout active — {vizData.assignedCount} of {vizData.totalSeats} seats assigned.
          </span>
          <Link
            to="#seating"
            className="ml-auto text-blue-800 font-semibold hover:underline whitespace-nowrap"
            onClick={() => navigate(`/events/${eventId}/guests#seating`)}
          >
            Assign Seats &rarr;
          </Link>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Compact Filters */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search guests or #group..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-8"
            compact
          />
          {activeGroupQuery && (
            <button
              onClick={clearGroupFilter}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              title="Clear group filter"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as RSVPStatus | 'all')}
          className="h-7 px-2 text-sm rounded-md border border-input bg-background"
          title="Filter by RSVP status"
        >
          <option value="all">All</option>
          <option value="accepted">✓ Accepted</option>
          <option value="invited">⏳ Invited</option>
          <option value="declined">✗ Declined</option>
          <option value="new">🆕 New</option>
        </select>
        <Button
          variant={showGroupPanel ? "secondary" : "outline"}
          size="sm"
          onClick={() => setShowGroupPanel(!showGroupPanel)}
          title="Groups"
        >
          <SlidersHorizontal className="h-3 w-3 mr-1" />
          Groups
        </Button>
      </div>

      {/* Active Group Filter Indicator */}
      {activeGroupQuery && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Filtered by:</span>
          <GroupBadge name={activeGroupQuery.replace(/^#/, '')} active size="sm" onClick={clearGroupFilter} />
          <span className="text-muted-foreground">({filteredGuests.length} results)</span>
        </div>
      )}

      {/* Group Panel */}
      {showGroupPanel && celebrationId && (
        <div className="p-3 bg-muted/20 rounded-lg border">
          <GroupManager
            celebrationId={celebrationId}
            onFilterByGroup={handleGroupFilter}
            activeGroupQuery={activeGroupQuery}
            onManageMembers={handleManageMembers}
            managingGroupId={managingGroupId}
            key={groupRefreshKey}
          />
        </div>
      )}

      {/* Group Member Mode Bar */}
      {managingGroupId && (
        <div className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <span className="text-blue-800">
            Selecting members for <strong>#{managingGroupName}</strong> — use checkboxes below
          </span>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSaveGroupMembers} disabled={savingMembers}>
              {savingMembers ? 'Saving...' : `Save (${groupMemberIds.length})`}
            </Button>
            <Button size="sm" variant="ghost" onClick={handleCancelGroupMembers}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Guest List */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="px-4 py-2 border-b bg-muted/30 flex items-center justify-between">
          <span className="text-sm font-medium">{filteredGuests.length} guests</span>
        </div>
        <GuestList
          guests={filteredGuests}
          onInviteCheckboxChange={managingGroupId ? handleGroupMemberToggle : () => undefined}
          selectedInviteIds={managingGroupId ? groupMemberIds : []}
          showSelection={!!managingGroupId}
          onGuestClick={(guest) => showGuestDetails(guest.id)}
          onEditGuest={canEditGuests ? handleEditGuest : undefined}
          onDeleteGuest={canAddGuests ? handleDeleteGuest : undefined}
          seatAssignments={seatAssignments}
          hideInviteActions={true}
        />
      </div>

      </>}

      {/* ===================== SEATING LAYOUT TAB ===================== */}
      {activeTab === 'seating' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Link
              to={`/events/${eventId}/seating`}
              className="flex items-center gap-1.5 text-primary hover:underline font-medium"
            >
              <LayoutGrid className="h-4 w-4" />
              Configure Seating Layout
            </Link>
            <span className="text-muted-foreground">(room &amp; layout settings)</span>
          </div>
          <SeatingBoard
            eventId={eventId}
            guests={guests}
            onAssignmentChange={handleAssignmentChange}
          />
        </div>
      )}

      {/* Guest Details Modal */}
      <GuestDetailsModal
        open={guestDetailsOpen}
        onClose={closeGuestDetailsModal}
        guest={guestDetails}
        loading={guestDetailsLoading}
        error={guestDetailsError}
      />

      {/* Edit Guest Form Modal - Compact */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-sm shadow-xl">
            <div className="flex items-center justify-between p-3 border-b">
              <h3 className="font-semibold text-sm">{editingGuest ? 'Edit Guest' : 'Add Guest'}</h3>
              <Button variant="ghost" size="icon-sm" onClick={resetForm}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              <div>
                <Label htmlFor="name" className="text-xs">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Guest name"
                  className={formErrors.name ? 'border-red-500' : ''}
                  compact
                />
                {formErrors.name && <p className="text-xs text-red-600 mt-0.5">{formErrors.name}</p>}
              </div>

              <div>
                <Label htmlFor="additional_guests" className="text-xs">Additional Guests</Label>
                <div className="flex items-center gap-2">
                  <input
                    id="allow_additional_guests"
                    type="checkbox"
                    checked={formData.is_additional_guest_allowed ?? false}
                    aria-label="Allow additional guests"
                    title="Allow additional guests"
                    onChange={(e) => {
                      const isAllowed = e.target.checked;
                      setFormData({
                        ...formData,
                        is_additional_guest_allowed: isAllowed,
                        additional_guest_count: isAllowed
                          ? Math.max(formData.additional_guest_count ?? 1, 1)
                          : 0,
                        additional_guest_type: 'count'
                      });
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <Label htmlFor="allow_additional_guests" className="text-xs">
                    Allow additional guests for this event
                  </Label>
                </div>
                {(formData.is_additional_guest_allowed) && (
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">You +</span>
                      <Input
                        id="additional_guests"
                        type="number"
                        min="1"
                        value={formData.additional_guest_count ?? 1}
                        onChange={(e) => setFormData({ ...formData, additional_guest_count: parseInt(e.target.value) || 0 })}
                        className="w-20"
                        compact
                      />
                      <span className="text-xs text-gray-500">guest{(formData.additional_guest_count ?? 0) !== 1 ? 's' : ''}</span>
                    </div>
                    <p className="text-xs text-gray-400">
                      This guest can bring up to {formData.additional_guest_count || 0} additional guest{(formData.additional_guest_count ?? 0) !== 1 ? 's' : ''} to this event.
                    </p>
                  </div>
                )}
                {!(formData.is_additional_guest_allowed) && (
                  <Input
                    id="additional_guests"
                    type="number"
                    min="0"
                    value={0}
                    disabled
                    className="mt-2"
                    compact
                  />
                )}
                {formErrors.additional_guests && (
                  <p className="text-xs text-red-600 mt-0.5">{formErrors.additional_guests}</p>
                )}
              </div>

              <div>
                <Label htmlFor="notes" className="text-xs">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Special requirements..."
                  rows={2}
                  className="text-sm"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="submit" size="sm" className="flex-1" disabled={submitting}>
                  {submitting ? 'Saving...' : (editingGuest ? 'Update' : 'Add')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={resetForm} disabled={submitting}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddGuestsPanel && celebrationId && canAddGuests && (
        <AddCelebrationGuestsPanel
          celebrationId={celebrationId}
          celebrationName={eventTitle}
          defaultEventId={eventId}
          onClose={() => setShowAddGuestsPanel(false)}
          onAdded={handleBulkGuestsSaved}
        />
      )}
    </PageContainer>
  );
};
